import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useEditorState } from './state';
import { useValueRef } from '../../../hooks/useValueRef';
import { eventNames } from '../config';

const Context = createContext();

const EditorWatchProvider = ({ children, emmiterOptions }) => {
  const { notify, subscribe } = emmiterOptions;

  const state = useEditorState();

  // data
  const selection = state.getSelection();
  const isSelectionCollapsed = selection.isCollapsed();
  const inlineStyle = state.getCurrentInlineStyle();
  const blockType = state
    .getCurrentContent()
    .getBlockForKey(selection.getStartKey())
    .getType();

  const dataRef = useValueRef({
    [eventNames.blockTypeChange]: blockType,
    [eventNames.inlineStyleChange]: inlineStyle,
    [eventNames.selectionCollapsedChange]: isSelectionCollapsed,
  });

  const getInitialValue = useCallback(
    (evtName) => dataRef.current[evtName],
    [dataRef]
  );

  useEffect(() => {
    notify(eventNames.blockTypeChange, blockType);
  }, [blockType, notify]);

  useEffect(() => {
    notify(eventNames.inlineStyleChange, inlineStyle);
  }, [inlineStyle, notify]);

  useEffect(() => {
    notify(eventNames.selectionCollapsedChange, isSelectionCollapsed);
  }, [isSelectionCollapsed, notify]);

  const value = useMemo(
    () => ({
      getInitialValue,
      subscribe,
    }),
    [getInitialValue, subscribe]
  );

  return <Context.Provider value={value}>{children}</Context.Provider>;
};

const useEditorWatch = (evtName) => {
  const { getInitialValue, subscribe } = useContext(Context);

  const [state, setState] = useState(() => getInitialValue(evtName));

  useEffect(() => subscribe(evtName, setState), [evtName, subscribe]);

  return state;
};

export { EditorWatchProvider, useEditorWatch };
